import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getSyncTemplates, getSegmentationOptions,
    simulateBulkRules, simulateBulkByPost, bulkCreatePriceRules,
    getAggressivenessOptions
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { getDomains } from '@/services/api';
import {
    ArrowLeft, ArrowRight, Check, Loader2, Globe, FileText,
    Search, ChevronDown, MapPin, Monitor, Layers, Percent,
    Zap, Clock, Settings2, FileCode, Smartphone, MonitorSmartphone
} from 'lucide-react';
import type {
    SimulateBulkResponse, BulkSimulationItem,
    SimulateBulkByPostResponse, BulkByPostSimulationItem,
    SyncTemplate
} from '@/types/price-rules';
import { CascadeDomainWarning } from './components/CascadeWarning';
import ScaleThresholdsInfo from './components/ScaleThresholdsInfo';

const STEPS = [
    { id: 1, name: 'Configuração' },
    { id: 2, name: 'Simulação' },
    { id: 3, name: 'Confirmação' },
];

const DEVICE_OPTIONS = [
    { value: '*', label: 'Todos', icon: '🌐' },
    { value: 'MOBILE', label: 'Mobile', icon: '📱' },
    { value: 'WEB', label: 'Web', icon: '💻' },
];

export default function CreateBulkPriceRules() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [currentStep, setCurrentStep] = useState(1);
    const [domainSearch, setDomainSearch] = useState('');
    const [domainOpen, setDomainOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');

    // Form state
    const [domainId, setDomainId] = useState<number | null>(null);
    const [domainName, setDomainName] = useState('');
    const [templateId, setTemplateId] = useState<number | null>(null);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [device, setDevice] = useState<string>('*');
    const [pmrTarget, setPmrTarget] = useState(70);
    const [syncInterval, setSyncInterval] = useState<number>(1440);
    const [ecpmReference, setEcpmReference] = useState<'rule_price' | 'gam_ecpm'>('rule_price');

    // Aggressiveness state
    const [useCustomInterval, setUseCustomInterval] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(1);
    const [customHours, setCustomHours] = useState(1);
    const [customMinutes, setCustomMinutes] = useState(0);

    // Mode: 'block' (default) or 'post' (block + id_post_wp)
    const [bulkMode, setBulkMode] = useState<'block' | 'post'>('block');

    // Simulation state
    const [simulation, setSimulation] = useState<SimulateBulkResponse | null>(null);
    const [postSimulation, setPostSimulation] = useState<SimulateBulkByPostResponse | null>(null);
    const [enabledItems, setEnabledItems] = useState<Record<string, boolean>>({});
    const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>({});

    // Queries
    const { data: domains, isLoading: domainsLoading } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });
    const { data: templates } = useQuery({
        queryKey: ['sync-templates'],
        queryFn: () => getSyncTemplates(),
    });
    const { data: segmentationOptions } = useQuery({
        queryKey: ['segmentation-options'],
        queryFn: getSegmentationOptions,
    });
    const { data: aggressivenessOptions } = useQuery({
        queryKey: ['aggressiveness-options'],
        queryFn: getAggressivenessOptions,
    });

    const activeTemplates = (templates || []).filter((t: SyncTemplate) => t.status === 'active');
    const selectedDomain = domains?.find((d: any) => d.id === domainId);
    const selectedTemplate = templates?.find((t: SyncTemplate) => t.id === templateId);

    const filteredDomains = domains?.filter((d: any) =>
        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
    ) || [];

    const countries = segmentationOptions?.countries || [];
    const filteredCountries = countries.filter((c: any) =>
        c.nome.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.codigo.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const getFaviconUrl = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    const getCountryLabel = (codigo: string) => {
        if (codigo === '*') return 'Todos';
        const c = countries.find((c: any) => c.codigo === codigo);
        return c ? `${c.nome} (${c.codigo})` : codigo;
    };

    const getItemKey = (adUnit: string, country: string, postId?: string) =>
        postId ? `${adUnit}_${country}_${postId}` : `${adUnit}_${country}`;
    const formatPrice = (price: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(price);
    const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);

    const getDeviceDisplay = (d: string) => {
        if (d === 'MOBILE') return { label: 'Mobile', icon: Smartphone, color: 'bg-violet-50 text-violet-700' };
        if (d === 'WEB') return { label: 'Web', icon: Monitor, color: 'bg-sky-50 text-sky-700' };
        return { label: 'Todos', icon: MonitorSmartphone, color: 'bg-gray-50 text-gray-600' };
    };

    // Aggressiveness helpers
    const levels = aggressivenessOptions?.levels || [];

    const handleLevelChange = (level: number) => {
        setSelectedLevel(level);
        const config = levels.find(l => l.level === level);
        if (config) setSyncInterval(config.interval_minutes);
    };

    const handleCustomIntervalChange = (hours: number, minutes: number) => {
        setCustomHours(hours);
        setCustomMinutes(minutes);
        const totalMinutes = (hours * 60) + minutes;
        const finalMinutes = Math.max(totalMinutes, aggressivenessOptions?.custom_min_minutes || 5);
        setSyncInterval(finalMinutes);
    };

    const formatInterval = (minutes: number) => {
        if (minutes >= 1440) {
            const days = minutes / 1440;
            return days === 1 ? '1 dia' : `${days} dias`;
        }
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            if (mins === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
            return `${hours}h ${mins}min`;
        }
        return `${minutes} minutos`;
    };

    const getLevelConfig = (level: number) => {
        const configs: Record<number, { color: string; bg: string; border: string; ring: string }> = {
            1: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-500' },
            2: { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-500' },
            3: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-500' },
            4: { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', ring: 'ring-rose-500' },
        };
        return configs[level] || { color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200', ring: 'ring-gray-500' };
    };

    const hourOptions = Array.from({ length: 169 }, (_, i) => i);
    const minuteOptions = [0, 5, 10, 15, 20, 30, 45];

    // Toggle country
    const toggleCountry = (codigo: string) => {
        setSelectedCountries(prev =>
            prev.includes(codigo) ? prev.filter(c => c !== codigo) : [...prev, codigo]
        );
    };

    // Simulate mutation
    const simulateMutation = useMutation({
        mutationFn: () => simulateBulkRules({
            domain_id: domainId!,
            domain_name: domainName,
            template_id: templateId,
            countries: selectedCountries,
            device: device as any,
            pmr_target: pmrTarget,
            sync_interval_minutes: syncInterval,
            ecpm_reference: ecpmReference,
        }),
        onSuccess: (data) => {
            setSimulation(data);
            // Enable all items that can be created by default
            const enabled: Record<string, boolean> = {};
            data.items.forEach((item: BulkSimulationItem) => {
                const key = getItemKey(item.ad_unit, item.country);
                enabled[key] = item.can_create && item.has_data;
            });
            setEnabledItems(enabled);
            setPriceOverrides({});
            setCurrentStep(2);
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao simular', description: error.response?.data?.detail || 'Erro ao consultar GAM.' });
        },
    });

    // Simulate by post mutation
    const simulatePostMutation = useMutation({
        mutationFn: () => simulateBulkByPost({
            domain_id: domainId!,
            domain_name: domainName,
            template_id: templateId,
            countries: selectedCountries,
            device: device as any,
            pmr_target: pmrTarget,
            sync_interval_minutes: syncInterval,
            ecpm_reference: ecpmReference,
        }),
        onSuccess: (data) => {
            setPostSimulation(data);
            const enabled: Record<string, boolean> = {};
            data.items.forEach((item: BulkByPostSimulationItem) => {
                const key = getItemKey(item.ad_unit, item.country, item.id_post_wp);
                const hasMinImpressions = (item.metrics?.impressions ?? 0) >= 100;
                enabled[key] = item.can_create && item.has_data && hasMinImpressions;
            });
            setEnabledItems(enabled);
            setPriceOverrides({});
            setCurrentStep(2);
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao simular', description: error.response?.data?.detail || 'Erro ao consultar GAM.' });
        },
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: () => {
            let rules;
            if (bulkMode === 'post' && postSimulation) {
                rules = (postSimulation.items || [])
                    .filter(item => enabledItems[getItemKey(item.ad_unit, item.country, item.id_post_wp)])
                    .map(item => ({
                        domain_id: domainId!,
                        template_id: templateId,
                        country: item.country,
                        ad_unit: item.ad_unit,
                        device: device as any,
                        custom_key: 'id_post_wp',
                        custom_value: item.id_post_wp,
                        price: priceOverrides[getItemKey(item.ad_unit, item.country, item.id_post_wp)] ?? item.suggested_price ?? 0.50,
                        pmr_target: pmrTarget,
                        sync_interval_minutes: syncInterval,
                        ecpm_reference: ecpmReference,
                    }));
            } else {
                rules = (simulation?.items || [])
                    .filter(item => enabledItems[getItemKey(item.ad_unit, item.country)])
                    .map(item => ({
                        domain_id: domainId!,
                        template_id: templateId,
                        country: item.country,
                        ad_unit: item.ad_unit,
                        device: device as any,
                        custom_key: null,
                        custom_value: null,
                        price: priceOverrides[getItemKey(item.ad_unit, item.country)] ?? item.suggested_price ?? 0.50,
                        pmr_target: pmrTarget,
                        sync_interval_minutes: syncInterval,
                        ecpm_reference: ecpmReference,
                    }));
            }

            return bulkCreatePriceRules({
                domain_id: domainId!,
                template_id: templateId,
                rules,
            });
        },
        onSuccess: (data) => {
            toast({ title: 'Regras criadas!', description: data.message || 'Operação concluída.' });
            queryClient.invalidateQueries({ queryKey: ['price-rules'] });
            navigate('/price-rules');
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao criar', description: error.response?.data?.detail || 'Erro ao criar regras.' });
        },
    });

    const enabledCount = Object.values(enabledItems).filter(Boolean).length;

    const canProceed = () => {
        switch (currentStep) {
            case 1: return !!domainId && !!templateId && selectedCountries.length > 0;
            case 2: return enabledCount > 0;
            case 3: return enabledCount > 0;
            default: return false;
        }
    };

    const isSimulating = simulateMutation.isPending || simulatePostMutation.isPending;

    const handleNext = () => {
        if (currentStep === 1) {
            if (bulkMode === 'post') {
                simulatePostMutation.mutate();
            } else {
                simulateMutation.mutate();
            }
        } else if (currentStep === 2) {
            setCurrentStep(3);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const toggleAll = (enabled: boolean) => {
        const newEnabled: Record<string, boolean> = {};
        if (bulkMode === 'post' && postSimulation) {
            (postSimulation.items || []).forEach(item => {
                const key = getItemKey(item.ad_unit, item.country, item.id_post_wp);
                newEnabled[key] = enabled && item.can_create && item.has_data;
            });
        } else {
            (simulation?.items || []).forEach(item => {
                const key = getItemKey(item.ad_unit, item.country);
                newEnabled[key] = enabled && item.can_create && item.has_data;
            });
        }
        setEnabledItems(newEnabled);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate('/price-rules')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Voltar
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Criar Regras em Massa</h1>
                    {domainName && <p className="text-gray-600">{domainName}</p>}
                </div>
            </div>

            {/* Progress */}
            <div className="bg-white rounded-lg shadow p-6">
                <ol className="flex items-center">
                    {STEPS.map((step, idx) => (
                        <li key={step.id} className={`relative ${idx !== STEPS.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
                            <div className="flex items-center">
                                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                    step.id < currentStep ? 'bg-green-600' : step.id === currentStep ? 'bg-blue-600' : 'bg-gray-200'
                                }`}>
                                    {step.id < currentStep ? (
                                        <Check className="h-5 w-5 text-white" />
                                    ) : (
                                        <span className={`text-sm font-medium ${step.id === currentStep ? 'text-white' : 'text-gray-500'}`}>{step.id}</span>
                                    )}
                                </div>
                                <span className={`ml-4 text-sm font-medium hidden sm:block ${step.id <= currentStep ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {step.name}
                                </span>
                            </div>
                            {idx !== STEPS.length - 1 && (
                                <div className={`absolute top-4 left-8 -ml-px h-0.5 w-full sm:w-20 ${step.id < currentStep ? 'bg-green-600' : 'bg-gray-200'}`} />
                            )}
                        </li>
                    ))}
                </ol>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-6 min-h-[400px]">
                {/* Step 1: Configuration */}
                {currentStep === 1 && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Configuração</h2>
                            <p className="text-sm text-gray-500 mt-1">Selecione domínio, template e países para gerar regras.</p>
                        </div>

                        {/* Bulk Mode Toggle */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1.5 bg-indigo-100 rounded-md"><Layers className="h-4 w-4 text-indigo-600" /></div>
                                Modo de criação
                            </Label>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setBulkMode('block')}
                                    className={`flex-1 h-12 px-4 rounded-xl border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                        bulkMode === 'block' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <Layers className="h-4 w-4" />
                                    Por Bloco
                                </button>
                                <button type="button" onClick={() => setBulkMode('post')}
                                    className={`flex-1 h-12 px-4 rounded-xl border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                        bulkMode === 'post' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <FileCode className="h-4 w-4" />
                                    Por Bloco + Post
                                </button>
                            </div>
                            {bulkMode === 'post' && (
                                <p className="text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">
                                    Busca automaticamente os posts com tráfego no GAM e cria regras por bloco x post (id_post_wp).
                                </p>
                            )}
                        </div>

                        {/* Domain Selection */}
                        <div className="space-y-3">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1.5 bg-blue-100 rounded-md"><Globe className="h-4 w-4 text-blue-600" /></div>
                                Domínio <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                                <button type="button" onClick={() => setDomainOpen(!domainOpen)} disabled={domainsLoading}
                                    className="w-full h-12 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-blue-500 disabled:opacity-50">
                                    {selectedDomain ? (
                                        <div className="flex items-center gap-3">
                                            <img src={getFaviconUrl(selectedDomain.domain)} alt="" className="w-5 h-5 rounded" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                            <span>{selectedDomain.domain}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">{domainsLoading ? 'Carregando...' : 'Selecione um domínio'}</span>
                                    )}
                                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${domainOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {domainOpen && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => { setDomainOpen(false); setDomainSearch(''); }} />
                                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                            <div className="p-2 border-b border-gray-100">
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                    <input type="text" placeholder="Buscar domínio..." value={domainSearch}
                                                        onChange={(e) => setDomainSearch(e.target.value)}
                                                        className="w-full h-10 pl-10 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                                                        onClick={(e) => e.stopPropagation()} autoFocus />
                                                </div>
                                            </div>
                                            <div className="max-h-[240px] overflow-y-auto">
                                                {filteredDomains.map((domain: any) => (
                                                    <button key={domain.id} type="button"
                                                        onClick={() => { setDomainId(domain.id); setDomainName(domain.domain); setDomainOpen(false); setDomainSearch(''); }}
                                                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${domainId === domain.id ? 'bg-blue-50' : ''}`}>
                                                        <div className="flex items-center gap-3">
                                                            <img src={getFaviconUrl(domain.domain)} alt="" className="w-5 h-5 rounded" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                                            <span className={domainId === domain.id ? 'font-medium text-blue-700' : ''}>{domain.domain}</span>
                                                        </div>
                                                        {domainId === domain.id && <Check className="h-4 w-4 text-blue-600" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <CascadeDomainWarning domainId={domainId} />

                        {/* Template Selection */}
                        <div className="space-y-3">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1.5 bg-purple-100 rounded-md"><FileText className="h-4 w-4 text-purple-600" /></div>
                                Template de Sync <span className="text-red-500">*</span>
                                <ScaleThresholdsInfo />
                            </Label>
                            {!domainId ? (
                                <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl text-center">
                                    <p className="text-sm text-gray-500">Selecione um domínio primeiro</p>
                                </div>
                            ) : activeTemplates.length === 0 ? (
                                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                                    <p className="text-sm text-yellow-800">Nenhum template ativo.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {activeTemplates.map((template: SyncTemplate) => (
                                        <button key={template.id} type="button" onClick={() => setTemplateId(template.id)}
                                            className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                                                templateId === template.id ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-gray-200 bg-white hover:border-gray-300'
                                            }`}>
                                            {templateId === template.id && (
                                                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-purple-500 shadow flex items-center justify-center">
                                                    <Check className="h-4 w-4 text-white" />
                                                </div>
                                            )}
                                            <p className="text-sm font-semibold text-gray-900">{template.name}</p>
                                            {template.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Device + PMR Target */}
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <div className="p-1.5 bg-purple-100 rounded-md"><Monitor className="h-4 w-4 text-purple-600" /></div>
                                    Device
                                </Label>
                                <div className="flex gap-2">
                                    {DEVICE_OPTIONS.map(d => (
                                        <button key={d.value} type="button" onClick={() => setDevice(d.value)}
                                            className={`flex-1 h-11 rounded-xl border-2 text-sm font-medium transition-all ${
                                                device === d.value ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'
                                            }`}>
                                            {d.icon} {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <div className="p-1.5 bg-blue-100 rounded-md"><Percent className="h-4 w-4 text-blue-600" /></div>
                                    PMR Target
                                </Label>
                                <div className="flex gap-2">
                                    {[50, 60, 70, 80, 90].map(v => (
                                        <button key={v} type="button" onClick={() => setPmrTarget(v)}
                                            className={`flex-1 h-11 rounded-xl border-2 text-sm font-medium transition-all ${
                                                pmrTarget === v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
                                            }`}>
                                            {v}%
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Aggressiveness + eCPM Reference */}
                        <div className="grid gap-4 md:grid-cols-2">
                            {/* Sync Interval / Aggressiveness */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                        <div className="p-1.5 bg-purple-100 rounded-md"><Zap className="h-4 w-4 text-purple-600" /></div>
                                        Agressividade do Sync
                                    </Label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className={`text-xs ${useCustomInterval ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                            Personalizado
                                        </span>
                                        <Switch
                                            checked={useCustomInterval}
                                            onCheckedChange={(v) => {
                                                setUseCustomInterval(v);
                                                if (!v) handleLevelChange(selectedLevel);
                                            }}
                                        />
                                    </label>
                                </div>

                                {!useCustomInterval ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        {levels.map((level) => {
                                            const config = getLevelConfig(level.level);
                                            const isSelected = selectedLevel === level.level;
                                            return (
                                                <button
                                                    key={level.level}
                                                    type="button"
                                                    onClick={() => handleLevelChange(level.level)}
                                                    className={`relative p-3 rounded-xl border-2 text-center transition-all ${config.bg} ${config.border} ${
                                                        isSelected ? `ring-2 ring-offset-1 ${config.ring}` : 'hover:shadow-md'
                                                    }`}
                                                >
                                                    <div className={`text-lg font-bold ${config.color}`}>{level.level}</div>
                                                    <div className={`text-xs font-semibold ${config.color}`}>{level.label}</div>
                                                    <div className="text-[10px] text-gray-500 mt-0.5">{level.description}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl bg-white">
                                        <Clock className="h-5 w-5 text-gray-400" />
                                        <div className="flex items-center gap-2">
                                            <div className="relative">
                                                <select
                                                    value={customHours}
                                                    onChange={(e) => handleCustomIntervalChange(parseInt(e.target.value), customMinutes)}
                                                    className="h-10 pl-3 pr-8 text-sm font-medium border-2 border-gray-200 rounded-lg bg-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500"
                                                >
                                                    {hourOptions.map((h) => (
                                                        <option key={h} value={h}>{h}h</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                            </div>
                                            <span className="text-gray-400">:</span>
                                            <div className="relative">
                                                <select
                                                    value={customMinutes}
                                                    onChange={(e) => handleCustomIntervalChange(customHours, parseInt(e.target.value))}
                                                    className="h-10 pl-3 pr-8 text-sm font-medium border-2 border-gray-200 rounded-lg bg-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500"
                                                >
                                                    {minuteOptions.map((m) => (
                                                        <option key={m} value={m}>{m}min</option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>
                                        <div className="flex-1 text-right">
                                            <span className="text-sm font-bold text-purple-600">{formatInterval(syncInterval)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* eCPM Reference */}
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <div className="p-1.5 bg-orange-100 rounded-md"><Settings2 className="h-4 w-4 text-orange-600" /></div>
                                    Base do Calculo
                                </Label>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setEcpmReference('rule_price')}
                                        className={`flex-1 h-11 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                            ecpmReference === 'rule_price'
                                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}>
                                        {ecpmReference === 'rule_price' && <Check className="h-4 w-4" />}
                                        Price da Regra
                                    </button>
                                    <button type="button" onClick={() => setEcpmReference('gam_ecpm')}
                                        className={`flex-1 h-11 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                            ecpmReference === 'gam_ecpm'
                                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                        }`}>
                                        {ecpmReference === 'gam_ecpm' && <Check className="h-4 w-4" />}
                                        eCPM do GAM
                                    </button>
                                </div>

                                {/* Interval summary */}
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500">Intervalo:</span>
                                        <span className="font-bold text-purple-600">{formatInterval(syncInterval)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm mt-1">
                                        <span className="text-gray-500">Base:</span>
                                        <span className="font-bold text-orange-600">{ecpmReference === 'rule_price' ? 'Price da Regra' : 'eCPM do GAM'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Countries Multi-select */}
                        <div className="space-y-3">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1.5 bg-green-100 rounded-md"><MapPin className="h-4 w-4 text-green-600" /></div>
                                Países <span className="text-red-500">*</span>
                                <span className="text-xs text-gray-400 ml-auto">{selectedCountries.length} selecionado(s)</span>
                            </Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input type="text" placeholder="Buscar país..." value={countrySearch}
                                    onChange={(e) => setCountrySearch(e.target.value)}
                                    className="w-full h-10 pl-10 pr-4 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500" />
                            </div>
                            <div className="flex gap-2 mb-2">
                                <button type="button" onClick={() => setSelectedCountries(countries.map((c: any) => c.codigo))}
                                    className="text-xs text-blue-600 hover:underline">Selecionar todos</button>
                                <button type="button" onClick={() => setSelectedCountries([])}
                                    className="text-xs text-gray-500 hover:underline">Limpar</button>
                            </div>
                            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto">
                                {filteredCountries.map((c: any) => (
                                    <button key={c.codigo} type="button" onClick={() => toggleCountry(c.codigo)}
                                        className={`px-3 py-2 text-xs rounded-lg border-2 text-left transition-all ${
                                            selectedCountries.includes(c.codigo)
                                                ? 'border-green-500 bg-green-50 text-green-700 font-medium'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}>
                                        {c.nome} ({c.codigo})
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Simulation Results */}
                {currentStep === 2 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Resultado da Simulação</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    {bulkMode === 'post' ? (
                                        <>{postSimulation?.total_simulated} combinações simuladas, {postSimulation?.posts_found} posts encontrados, {postSimulation?.total_with_data} com dados, {enabledCount} selecionada(s)</>
                                    ) : (
                                        <>{simulation?.total_simulated} combinações simuladas, {simulation?.total_with_data} com dados, {enabledCount} selecionada(s)</>
                                    )}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => toggleAll(true)}>Selecionar todos</Button>
                                <Button variant="outline" size="sm" onClick={() => toggleAll(false)}>Desmarcar todos</Button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-3 px-2 font-medium text-gray-500">Ativo</th>
                                        <th className="text-left py-3 px-2 font-medium text-gray-500">Bloco</th>
                                        {bulkMode === 'post' && (
                                            <th className="text-left py-3 px-2 font-medium text-gray-500">Post</th>
                                        )}
                                        <th className="text-left py-3 px-2 font-medium text-gray-500">País</th>
                                        <th className="text-center py-3 px-2 font-medium text-gray-500">Device</th>
                                        <th className="text-right py-3 px-2 font-medium text-gray-500">eCPM</th>
                                        <th className="text-right py-3 px-2 font-medium text-gray-500">PMR</th>
                                        <th className="text-right py-3 px-2 font-medium text-gray-500">Impressões</th>
                                        <th className="text-right py-3 px-2 font-medium text-gray-500">Preço</th>
                                        <th className="text-center py-3 px-2 font-medium text-gray-500">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkMode === 'post' ? (
                                        (postSimulation?.items || []).map((item, idx) => {
                                            const key = getItemKey(item.ad_unit, item.country, item.id_post_wp);
                                            const isEnabled = enabledItems[key] || false;
                                            const currentPrice = priceOverrides[key] ?? item.suggested_price;

                                            return (
                                                <tr key={idx} className={`border-b border-gray-100 ${!item.has_data ? 'opacity-50' : ''}`}>
                                                    <td className="py-2 px-2">
                                                        <Switch
                                                            checked={isEnabled}
                                                            onCheckedChange={(v) => setEnabledItems(prev => ({ ...prev, [key]: v }))}
                                                            disabled={!item.can_create || !item.has_data}
                                                            className="data-[state=checked]:bg-green-500"
                                                        />
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md font-medium">
                                                            <Layers className="h-3 w-3" />{item.ad_unit}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <div className="flex flex-col max-w-[250px]">
                                                            {item.slug ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-md font-medium truncate" title={`/${item.slug}`}>
                                                                    <FileCode className="h-3 w-3 shrink-0" />/{item.slug}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-md font-medium">
                                                                    <FileCode className="h-3 w-3" />Post #{item.id_post_wp}
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] text-gray-400 mt-0.5 pl-1">
                                                                ID: {item.id_post_wp}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <span className="text-xs">{getCountryLabel(item.country)}</span>
                                                    </td>
                                                    <td className="py-2 px-2 text-center">
                                                        {(() => { const dv = getDeviceDisplay(device); const DvIcon = dv.icon; return (
                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md font-medium ${dv.color}`}>
                                                                <DvIcon className="h-3 w-3" />{dv.label}
                                                            </span>
                                                        ); })()}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? formatPrice(item.metrics.ecpm) : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? `${item.metrics.pmr.toFixed(1)}%` : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? formatNumber(item.metrics.impressions) : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right">
                                                        {item.has_data && item.suggested_price ? (
                                                            <input type="number" min={0.5} max={3000} step={0.5}
                                                                value={currentPrice ?? ''}
                                                                onChange={(e) => {
                                                                    const v = parseFloat(e.target.value);
                                                                    if (!isNaN(v)) setPriceOverrides(prev => ({ ...prev, [key]: v }));
                                                                }}
                                                                className="w-20 h-7 text-xs text-right border border-gray-200 rounded-md px-2 focus:outline-none focus:border-blue-500" />
                                                        ) : (
                                                            <span className="text-xs text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-2 text-center">
                                                        {!item.has_data ? (
                                                            <span className="text-xs text-gray-400">Sem dados</span>
                                                        ) : !item.can_create ? (
                                                            <span className="text-xs text-yellow-600">Duplicada</span>
                                                        ) : (
                                                            <span className="text-xs text-green-600">OK</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        (simulation?.items || []).map((item, idx) => {
                                            const key = getItemKey(item.ad_unit, item.country);
                                            const isEnabled = enabledItems[key] || false;
                                            const currentPrice = priceOverrides[key] ?? item.suggested_price;

                                            return (
                                                <tr key={idx} className={`border-b border-gray-100 ${!item.has_data ? 'opacity-50' : ''}`}>
                                                    <td className="py-2 px-2">
                                                        <Switch
                                                            checked={isEnabled}
                                                            onCheckedChange={(v) => setEnabledItems(prev => ({ ...prev, [key]: v }))}
                                                            disabled={!item.can_create || !item.has_data}
                                                            className="data-[state=checked]:bg-green-500"
                                                        />
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md font-medium">
                                                            <Layers className="h-3 w-3" />{item.ad_unit}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-2">
                                                        <span className="text-xs">{getCountryLabel(item.country)}</span>
                                                    </td>
                                                    <td className="py-2 px-2 text-center">
                                                        {(() => { const dv = getDeviceDisplay(device); const DvIcon = dv.icon; return (
                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md font-medium ${dv.color}`}>
                                                                <DvIcon className="h-3 w-3" />{dv.label}
                                                            </span>
                                                        ); })()}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? formatPrice(item.metrics.ecpm) : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? `${item.metrics.pmr.toFixed(1)}%` : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right font-mono text-xs">
                                                        {item.metrics ? formatNumber(item.metrics.impressions) : '-'}
                                                    </td>
                                                    <td className="py-2 px-2 text-right">
                                                        {item.has_data && item.suggested_price ? (
                                                            <input type="number" min={0.5} max={3000} step={0.5}
                                                                value={currentPrice ?? ''}
                                                                onChange={(e) => {
                                                                    const v = parseFloat(e.target.value);
                                                                    if (!isNaN(v)) setPriceOverrides(prev => ({ ...prev, [key]: v }));
                                                                }}
                                                                className="w-20 h-7 text-xs text-right border border-gray-200 rounded-md px-2 focus:outline-none focus:border-blue-500" />
                                                        ) : (
                                                            <span className="text-xs text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-2 text-center">
                                                        {!item.has_data ? (
                                                            <span className="text-xs text-gray-400">Sem dados</span>
                                                        ) : !item.can_create ? (
                                                            <span className="text-xs text-yellow-600">Duplicada</span>
                                                        ) : (
                                                            <span className="text-xs text-green-600">OK</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Step 3: Confirmation */}
                {currentStep === 3 && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Confirmação</h2>
                            <p className="text-sm text-gray-500 mt-1">Revise as regras antes de criar.</p>
                        </div>

                        {/* Summary */}
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <p className="text-2xl font-bold text-blue-900">{enabledCount}</p>
                                    <p className="text-xs text-blue-600">Regras a criar</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-blue-900">{domainName}</p>
                                    <p className="text-xs text-blue-600">Domínio</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-blue-900">{selectedTemplate?.name}</p>
                                    <p className="text-xs text-blue-600">Template</p>
                                </div>
                            </div>
                        </div>

                        {/* Rules list */}
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {bulkMode === 'post' ? (
                                (postSimulation?.items || [])
                                    .filter(item => enabledItems[getItemKey(item.ad_unit, item.country, item.id_post_wp)])
                                    .map((item, idx) => {
                                        const key = getItemKey(item.ad_unit, item.country, item.id_post_wp);
                                        const price = priceOverrides[key] ?? item.suggested_price ?? 0;
                                        return (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium">{item.ad_unit}</span>
                                                    <span className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded font-medium max-w-[200px] truncate" title={item.slug ? `/${item.slug} (ID: ${item.id_post_wp})` : `Post #${item.id_post_wp}`}>
                                                        {item.slug ? `/${item.slug}` : `#${item.id_post_wp}`}
                                                    </span>
                                                    <span className="text-sm text-gray-600">{getCountryLabel(item.country)}</span>
                                                    {(() => { const dv = getDeviceDisplay(device); const DvIcon = dv.icon; return (
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded font-medium ${dv.color}`}>
                                                            <DvIcon className="h-3 w-3" />{dv.label}
                                                        </span>
                                                    ); })()}
                                                </div>
                                                <span className="font-mono font-semibold text-sm">{formatPrice(price)}</span>
                                            </div>
                                        );
                                    })
                            ) : (
                                (simulation?.items || [])
                                    .filter(item => enabledItems[getItemKey(item.ad_unit, item.country)])
                                    .map((item, idx) => {
                                        const key = getItemKey(item.ad_unit, item.country);
                                        const price = priceOverrides[key] ?? item.suggested_price ?? 0;
                                        return (
                                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium">{item.ad_unit}</span>
                                                    <span className="text-sm text-gray-600">{getCountryLabel(item.country)}</span>
                                                    {(() => { const dv = getDeviceDisplay(device); const DvIcon = dv.icon; return (
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded font-medium ${dv.color}`}>
                                                            <DvIcon className="h-3 w-3" />{dv.label}
                                                        </span>
                                                    ); })()}
                                                </div>
                                                <span className="font-mono font-semibold text-sm">{formatPrice(price)}</span>
                                            </div>
                                        );
                                    })
                            )}
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {isSimulating && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-blue-200 rounded-full"></div>
                            <div className="w-16 h-16 border-4 border-blue-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                        </div>
                        <p className="mt-6 text-lg font-medium text-gray-700">Simulando regras...</p>
                        <p className="mt-2 text-sm text-gray-500">
                            {bulkMode === 'post'
                                ? `Buscando posts com tráfego e simulando para ${selectedCountries.length} país(es)`
                                : `Consultando GAM para ${selectedCountries.length} país(es) x 8 blocos`
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
                <Button variant="outline" onClick={handleBack} disabled={currentStep === 1 || isSimulating || createMutation.isPending}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Voltar
                </Button>

                {currentStep < 3 ? (
                    <Button onClick={handleNext} disabled={!canProceed() || isSimulating}>
                        {isSimulating ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Simulando...</>
                        ) : (
                            <>{currentStep === 1 ? 'Simular' : 'Confirmar'}<ArrowRight className="ml-2 h-4 w-4" /></>
                        )}
                    </Button>
                ) : (
                    <Button onClick={() => createMutation.mutate()} disabled={!canProceed() || createMutation.isPending} className="bg-green-600 hover:bg-green-700">
                        {createMutation.isPending ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando...</>
                        ) : (
                            <><Check className="mr-2 h-4 w-4" />Criar {enabledCount} regras</>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}
